import { MouseEventHandler, ReactNode, useCallback } from 'react';
import styled from 'styled-components';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import { ROW_HEIGHT } from '#/components/rfq-common/TableRow';
import useIsMobile from '@paradigm/design-system/src/hooks/useIsMobile';
import {
  useRfqExpansionState,
  useToggleRfqExpansion,
} from '#/components/UnifiedRfqs/rfqs/rfq-expansion-context';
import RfqRowBase, {
  tableCols,
} from '#/components/UnifiedRfqs/components/RfqRowBase';

interface Props {
  readonly rfqId: string;
  readonly children: ReactNode;
  readonly onContextMenu?: MouseEventHandler;
  readonly isExpandable?: boolean;
}

export default function ExpandableRfqRow({
  rfqId,
  children,
  onContextMenu,
  isExpandable = true,
}: Props) {
  const isMobile = useIsMobile();

  const toggleRfqExpansion = useToggleRfqExpansion();
  const isExpanded = useRfqExpansionState(rfqId) && !isMobile;
  const toggleRowState = useCallback(() => {
    if (isExpandable) toggleRfqExpansion(rfqId);
  }, [isExpandable, rfqId, toggleRfqExpansion]);
  return (
    <RfqRow
      onContextMenu={onContextMenu}
      data-testid={`rfq-row-${rfqId}`}
      isExpanded={isExpanded}
      onClick={toggleRowState}
    >
      {children}
    </RfqRow>
  );
}

interface RowProps {
  readonly isExpanded: boolean;
}
const RfqRow = styled(RfqRowBase).withConfig<RowProps>({
  shouldForwardProp: (prop) => !['isExpanded'].includes(prop),
})`
  --row-arrow-display: ${({ isExpanded }) => (isExpanded ? 'none' : 'block')};
  height: ${({ isExpanded }) => (isExpanded ? 'auto' : `${ROW_HEIGHT}px`)};
  align-items: ${({ isExpanded }) => (isExpanded ? 'start' : 'center')};

  ${tableCols}
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    height: 100%;
    --row-arrow-display: none;
  }
`;
